import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { withTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import PropTypes from 'prop-types';
import { t } from 'i18next';

import { encryptCheckout } from '../../../utils/encryptionUtility';
import { useValidation } from '../../../hooks/useValidation';
import { membershipInfoList } from '../../../constants/memberships';
import { useMembership } from '../../../contexts/MembershipServiceContext';
import {
	getCustomerDetails,
	postBrowserDetails,
	postCustomerDetails,
	postCustomerDetailsReset
} from '../../../containers/customerDetails/customerDetailsSlice';
import { getBrowserSignature } from '../../../utils/getBrowserSignature';

import MembershipInfo from '../../../components/MembershipInfo';
import PersonalInfo from '../../../components/PersonalInfo';
import PaymentInfo from '../../../components/PaymentInfo';

import Alert from '@mui/material/Alert';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import LoadingButton from '@mui/lab/LoadingButton';
import { Link } from '@mui/material';

const Home = () => {
	const dispatch = useDispatch();
	const signatureObj = getBrowserSignature();
	const [initialValues, setInitialValues] = React.useState({
		firstName: '',
		lastName: '',
		address1: '',
		address2: '',
		city: '',
		state: '',
		zipCode: '',
		email: '',
		phone: '',
		cardNumber: '',
		expiryDate: '',
		cvc: '',
		nameOnCard: '',
		typeOfCard: ''
	});
	const [isFormComplete, setIsFormComplete] = React.useState(false);

	const { isLoading: membershipDataLoading, data: membershipData } =
		useSelector((state) => state.customerDetailsSlice.customerDetails);

	const { isLoading: postBrowserDataLoading } = useSelector(
		(state) => state.customerDetailsSlice.postBrowserDetails
	);

	const {
		isLoading: submitDataIsLoading,
		error: submitDataError,
		hasCompleted: submitCompleted
	} = useSelector((state) => state.customerDetailsSlice.postCustomerDetails);

	const { setMembershipService } = useMembership();

	const { paylinkId } = useParams();

	const membershipListData =
		membershipInfoList[membershipData?.clubCategoryNumber];

	React.useEffect(() => {
		if (submitCompleted) {
			setIsFormComplete(true);
			dispatch(postCustomerDetailsReset());
		}
		if (paylinkId) {
			dispatch(getCustomerDetails(paylinkId));
			dispatch(
				postBrowserDetails({
					paylinkId,
					browserSignature: signatureObj.signatureKey
				})
			);
		}
	}, [paylinkId, submitCompleted]);

	React.useEffect(() => {
		if (membershipData) {
			setMembershipService(membershipListData);
			setInitialValues({
				firstName: membershipData.firstName || '',
				lastName: membershipData.lastName || '',
				address1: membershipData.address1 || '',
				address2: membershipData.address2 || '',
				city: membershipData.city || '',
				state: membershipData.state || '',
				zipCode: membershipData.zip || '',
				email: '', // intentionally left blank
				phone: membershipData.phoneNumber || '',
				cardNumber: '',
				expiryDate: '',
				cvc: '',
				nameOnCard: '',
				typeOfCard: ''
			});
		}
	}, [membershipData]);

	const validationRules = {
		firstName: t('personalInfo:firstName'),
		lastName: t('personalInfo:lastName'),
		address1: t('personalInfo:address1'),
		city: t('personalInfo:city'),
		state: t('personalInfo:state'),
		zipCode: t('personalInfo:zipCode'),
		email: t('personalInfo:email'),
		phone: t('personalInfo:phone'),
		cardNumber: t('paymentInfo:cardNumber'),
		expiryDate: t('paymentInfo:expiryDate'),
		cvc: t('paymentInfo:cvv'),
		nameOnCard: t('paymentInfo:nameOnCard')
	};

	const {
		formData,
		errors,
		handleInputChange,
		handleInputBlur,
		validateForm,
		reset
	} = useValidation(initialValues, validationRules, t);

	const paymentInfoRef = React.useRef();

	const handleSubmit = () => {
		if (validateForm()) {
			const {
				firstName,
				lastName,
				address1,
				address2,
				city,
				state,
				zipCode,
				email,
				phone,
				cardNumber,
				expiryDate,
				cvc,
				nameOnCard,
				typeOfCard
			} = formData;
			const unformattedCardNumber = cardNumber.replace(/[ ]/g, '');
			const unformattedExpireDate = expiryDate.replace(/[ /]/g, '');

			const payload = {
				cardInfo: encryptCheckout({
					cardNumber: unformattedCardNumber,
					expiryDate: unformattedExpireDate,
					cvv: cvc
				}),
				nameOnCard,
				typeOfCard: typeOfCard[0]?.toUpperCase() || '',
				firstName,
				lastName,
				address1,
				address2,
				city,
				state,
				zip: zipCode.replace(/\D/g, ''),
				country: 'US',
				emailAddress: email,
				phoneNumber: phone.replace(/\D/g, ''),
				browserSignature: signatureObj.signatureKey,
				paylinkId
			};

			dispatch(postCustomerDetails(payload));
			reset();
			paymentInfoRef.current?.clearFields();
		}
	};

	if (membershipDataLoading || postBrowserDataLoading) {
		return <Typography>{t('home:loadingText')}</Typography>;
	}

	if (membershipData?.status === false && membershipData?.clubCategoryNumber) {
		return (
			<Stack gap={3} alignItems='center'>
				<img
					src={membershipListData?.logo}
					alt={membershipListData.name}
					width={280}
				/>
				<Stack gap={2}>
					{isFormComplete && (
						<Typography>
							We successfully updated your payment information. Keep an eye out
							for an charge on your statement within the next 48 hours.
						</Typography>
					)}
					<Typography>
						{t('home:linkNotValid')}
						<Link href='mailto:support@sempris.com'>support@sempris.com</Link>
					</Typography>
				</Stack>
			</Stack>
		);
	}

	if (!paylinkId || !membershipListData) {
		return (
			<Stack>
				<Typography>{t('home:missingInfo')}</Typography>
			</Stack>
		);
	}

	return (
		<Stack gap={3} padding='20px 0'>
			{submitDataError && (
				<Alert variant='filled' severity='error'>
					{t('home:error')}
				</Alert>
			)}
			<MembershipInfo
				premiumOffer={membershipData?.premiumOfferDescription}
				membershipTypeCode={membershipData?.membershipTypeCode}
				membershipListData={membershipListData}
			/>
			<PersonalInfo
				formData={formData}
				errors={errors}
				handleInputChange={handleInputChange}
				handleInputBlur={handleInputBlur}
			/>
			<PaymentInfo
				ref={paymentInfoRef}
				formData={formData}
				errors={errors}
				handleInputChange={handleInputChange}
				handleInputBlur={handleInputBlur}
			/>
			<Stack>
				<LoadingButton
					variant='contained'
					type='submit'
					color='primary'
					onClick={handleSubmit}
					loading={submitDataIsLoading}
				>
					{t('home:submit')}
				</LoadingButton>
			</Stack>
		</Stack>
	);
};

Home.propTypes = {
	t: PropTypes.func
};

export default withTranslation('Home')(Home);
